import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import api from 'app/utils/axiosInstance';
import colors from 'app/utils/colors';

const getToken = () => localStorage.getItem('access_token');

export default function TwoFactorSetup({ show, onClose }) {
  const { t } = useTranslation('settings');
  const [step, setStep] = useState('idle');
  const [qrCode, setQrCode] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const twofaEnabled = user?.twoFactorEnabled;
  const showOnlyDone = step === 'done' || twofaEnabled;

  const resetAndClose = () => {
    setStep('idle');
    setQrCode('');
    setToken('');
    setError('');
    setLoading(false);
    onClose?.();
  };

  const handleEnable = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post(
        '/auth/enable-2fa',
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setQrCode(data.qrCodeDataUrl);
      setStep('qr');
    } catch {
      setError(t('twoFactorAuth.errors.qrFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (token.length !== 6) {
      setError(t('twoFactorAuth.errors.codeRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post(
        '/auth/verify-2fa',
        { token },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setStep('done');
    } catch {
      setError(t('twoFactorAuth.errors.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div style={styles.overlay} onClick={resetAndClose}>
      <div style={styles.card} onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={resetAndClose}
          style={styles.closeBtn}
        >
          ×
        </button>

        {/* ── Done content (replaces header + body) ── */}
        {showOnlyDone ? (
          <div style={styles.doneBody}>
            <div style={styles.doneIconCircle}>
              <img
                src="/assets/icons/settingsIcons/CHECK.png"
                alt=""
                style={styles.doneCheckImg}
              />
            </div>
            <p style={styles.doneTitle}>{t('twoFactorAuth.done.title')}</p>
            <p style={styles.doneSubtitle}>{t('twoFactorAuth.done.subtitle')}</p>
          </div>
        ) : (
          <>
            {/* ── Header ── */}
            <div style={styles.header}>
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: colors.backgroundLight,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto 10px',
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    backgroundColor: colors.primary,
                    WebkitMaskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                    maskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                    display: 'inline-block',
                  }}
                />
              </div>
              <h2 style={styles.title}>{t('twoFactorAuth.title')}</h2>
              <p style={styles.subtitle}>{t('twoFactorAuth.subtitle')}</p>
            </div>
            <div style={styles.separator} />

            {/* ── Scrollable content area ── */}
            <div style={styles.scrollArea}>
              {/* ── Step: idle ── */}
              {step === 'idle' && (
                <div style={styles.body}>
                  <p style={styles.description}>{t('twoFactorAuth.idle.description')}</p>
                  <button
                    style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                    onClick={handleEnable}
                    disabled={loading}
                  >
                    {loading ? t('twoFactorAuth.idle.enabling') : t('twoFactorAuth.idle.enable')}
                  </button>
                </div>
              )}

              {/* ── Step: qr ── */}
              {step === 'qr' && (
                <div style={styles.body}>
                  <ol style={styles.steps}>
                    <li>{t('twoFactorAuth.qr.step1')}</li>
                    <li>{t('twoFactorAuth.qr.step2')}</li>
                    <li>{t('twoFactorAuth.qr.step3')}</li>
                  </ol>

                  <div style={styles.qrWrapper}>
                    <img src={qrCode} alt="" style={styles.qr} />
                  </div>

                  <p style={styles.label}>{t('twoFactorAuth.qr.codeLabel')}</p>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder={t('twoFactorAuth.qr.codePlaceholder')}
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, ''))}
                    style={styles.input}
                  />
                  <button
                    style={{ ...styles.btn, ...(loading ? styles.btnDisabled : {}) }}
                    onClick={handleVerify}
                    disabled={loading}
                  >
                    {loading ? t('twoFactorAuth.qr.confirming') : t('twoFactorAuth.qr.confirm')}
                  </button>
                </div>
              )}

              {/* ── Error ── */}
              {error && <p style={styles.error}>{error}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
    padding: '24px',
    boxSizing: 'border-box',
  },
  card: {
    position: 'relative',
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 14,
    background: 'transparent',
    border: 'none',
    fontSize: '22px',
    lineHeight: 1,
    color: '#9ca3af',
    cursor: 'pointer',
    padding: 4,
    zIndex: 1,
  },
  header: {
    background: 'white',
    padding: '28px 32px 20px',
    textAlign: 'center',
    flexShrink: 0,
  },
  title: {
    margin: '0 0 8px',
    fontSize: '20px',
    fontWeight: '700',
    color: 'black',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#8E8E8E',
  },
  separator: {
    height: '1px',
    backgroundColor: '#E5E7EB',
    margin: '0 32px',
    flexShrink: 0,
  },
  scrollArea: {
    overflowY: 'auto',
  },
  body: {
    padding: '24px 32px 32px',
  },
  description: {
    fontSize: '14px',
    color: '#8E8E8E',
    lineHeight: '1.6',
    marginBottom: '24px',
  },
  steps: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.8',
    paddingLeft: '20px',
    marginBottom: '16px',
  },
  qrWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
    padding: '10px',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    background: '#fafafa',
  },
  qr: {
    width: '150px',
    height: '150px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '10px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '24px',
    fontWeight: '700',
    letterSpacing: '6px',
    textAlign: 'center',
    border: '2px solid #e5e7eb',
    borderRadius: '10px',
    outline: 'none',
    marginBottom: '16px',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    transition: 'border-color 0.2s',
  },
  btn: {
    width: '100%',
    padding: '13px',
    background: `${colors.primary}`,
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  btnDisabled: {
    background: '#a5b4fc',
    cursor: 'not-allowed',
  },
  error: {
    margin: '0 32px 24px',
    padding: '10px 14px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#dc2626',
  },
  doneBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '64px 32px',
    textAlign: 'center',
  },
  doneIconCircle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '16px',
  },
  doneCheckImg: {
    width: 64,
    height: 64,
  },
  doneTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 8px',
  },
  doneSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.6',
    margin: 0,
  },
};